<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import { buildAssessmentAnswerPayload } from "#bb/lib/assessmentAnswers";

const logger = useLogger("Select");

// 1) Expect 'id' from the parent.
const props = defineProps<{ id: string }>();
const emit = defineEmits<{
  (e: "content-update", value: string): void;
  (e: "answer-error", payload: { id: string; error: unknown }): void;
}>();

// 2) Get the definition data from the content manager.
const { getContentById, getResolvedDataPath } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

// 3) Inject features (e.g. editor mode).
const features = inject("render-features", { editor: false });

// 4) Define commands for the context menu.
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Building block editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the options",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Single Select",
    icon: "lucide:check",
  },
});

// 5) Setup assessment answer binding (single value)
const { getAnswer, getCachedAnswer, setAnswer } = useAnswers();

const answer = ref<string>("");
const answerPath = computed(() => getResolvedDataPath(props.id).join("/"));

// Reactively mirror the shared answer cache so an answer made on any surface (the page or the AI
// chat) is reflected here live; getAnswer() ensures the cache has been loaded at least once.
watch(
  () => getCachedAnswer(props.id, answerPath.value)?.value,
  (value) => {
    const next = (value ?? "") as string;
    if (next !== answer.value) {
      answer.value = next;
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (props.id) {
    void getAnswer(props.id, answerPath.value);
  }
});

const { error, validate } = useFormField({
  id: props.id,
  componentId: ComponentDefinitionIds.SELECT_INPUT,
  getValue: () => answer.value,
  getValidation: () => definitionData.value?.validation,
  inputType: "string",
});

const save = async (newAnswer: string) => {
  answer.value = newAnswer;
  if (validate().ok === false || !props.id || !definitionData.value) {
    return;
  }

  try {
    await setAnswer(
      props.id,
      buildAssessmentAnswerPayload({
        block: definitionData.value,
        componentId: ComponentDefinitionIds.SELECT_INPUT,
        value: answer.value,
        path: answerPath.value,
      }),
      { throwOnError: true },
    );
    emit("content-update", answer.value);
  } catch (error) {
    emit("answer-error", { id: props.id, error });
  }
};
</script>

<template>
  <div :class="cn('single-select', $attrs.class ?? '')">
    <!-- Use your Select component -->
    <Select :model-value="answer" @update:model-value="save">
      <SelectTrigger class="w-full" :aria-invalid="!!error" :class="error ? 'border-destructive' : ''">
        <SelectValue :placeholder="definitionData.placeholder" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem
            v-for="(option, index) in definitionData.options"
            :key="option.id || index"
            :value="option.id"
          >
            {{ option.name }}
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
    <p v-if="error" class="text-destructive text-sm mt-1">{{ error }}</p>
  </div>

  <!-- Editor UI (only visible in editor mode) -->
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.SELECT_INPUT,
  editor: () => import("#bb/components/editor/assessment/select.vue"),
  icon: "lucide:check",
  type: "Single Select",
  description: "Select a single option from a list of choices",
  default: {
    options: [
      { id: crypto.randomUUID(), name: "Option 1", value: 1 },
      { id: crypto.randomUUID(), name: "Option 2", value: 2 },
    ],
    placeholder: "Select an option",
    selected: null,
  },
  categories: ["Assessment"],
  order: 10003,
};
</script>
